import { motion } from 'framer-motion'

const PointsPhoneMockup = () => {
  return (
    <div className="relative">
      <div className="w-80 h-[640px] bg-gray-900 rounded-[3rem] p-3 shadow-2xl border-2 border-gray-700">
        <div className="w-full h-full bg-black rounded-[2.5rem] overflow-hidden border border-gray-800">
          <motion.div 
            className="p-5"
            animate={{ y: [0, -400, 0] }}
            transition={{ duration: 8, repeat: Infinity, ease: "easeInOut", repeatDelay: 2 }}
          >
            {/* Status Bar */}
            <div className="flex justify-between items-center mb-6 text-sm font-semibold text-white">
              <span>9:41</span>
              <div className="flex gap-1">
                <div className="w-4 h-3 bg-gray-600 rounded-sm"></div>
                <div className="w-4 h-3 bg-gray-600 rounded-sm"></div>
                <div className="w-4 h-3 bg-gray-600 rounded-sm"></div>
              </div>
            </div>

            {/* Hexagon Logo */}
            <motion.div
              initial={{ opacity: 0, scale: 0.8 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ delay: 0.2, duration: 0.6 }}
              className="flex justify-center mb-6"
            >
              <motion.svg
                width="80"
                height="80"
                viewBox="0 0 100 100"
                animate={{ rotate: [0, 5, -5, 5, 0] }}
                transition={{ delay: 1, duration: 2, repeat: Infinity, repeatDelay: 4 }}
              >
                <defs>
                  <linearGradient id="hexGrad" x1="0%" y1="0%" x2="100%" y2="100%">
                    <stop offset="0%" style={{ stopColor: '#FF2D87', stopOpacity: 1 }} />
                    <stop offset="50%" style={{ stopColor: '#C850C0', stopOpacity: 1 }} />
                    <stop offset="100%" style={{ stopColor: '#7B2FBE', stopOpacity: 1 }} />
                  </linearGradient>
                </defs>
                <path
                  d="M50 5 L90 30 L90 70 L50 95 L10 70 L10 30 Z"
                  fill="url(#hexGrad)"
                  stroke="white"
                  strokeWidth="2"
                />
                <text
                  x="50"
                  y="68"
                  fontSize="48"
                  fill="white"
                  fontWeight="bold"
                  textAnchor="middle"
                  fontFamily="system-ui"
                >
                  A
                </text>
              </motion.svg>
            </motion.div>

            {/* Points Balance Card */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.4, duration: 0.5 }}
              className="relative mb-6 rounded-3xl p-6 overflow-hidden"
              style={{
                background: 'linear-gradient(135deg, #FF2D87 0%, #C850C0 50%, #7B2FBE 100%)'
              }}
            >
              {/* Tier Badge - Top Right */}
              <div className="absolute top-4 right-4">
                <div className="flex items-center gap-1.5 px-3 py-1.5 bg-white/20 backdrop-blur-md rounded-full border-2 border-gray-300">
                  <span className="text-yellow-200 text-sm">⭐</span>
                  <span className="text-xs font-bold text-white">Silver</span>
                </div>
              </div>

              {/* Points Display */}
              <div className="mb-4">
                <div className="text-sm text-white/80 mb-1 font-medium">AzrefyPoints</div>
                <motion.div
                  initial={{ opacity: 0, scale: 0.9 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ delay: 0.6, duration: 0.5 }}
                  className="text-5xl font-bold text-white"
                >
                  2,450
                </motion.div>
              </div>

              {/* Monthly Stats - Glassmorphic */}
              <div className="bg-white/10 backdrop-blur-md rounded-2xl p-3 border border-white/20">
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <div className="text-xs text-white/70 mb-1">This Month</div>
                    <div className="text-lg font-bold text-white">27 pts</div>
                  </div>
                  <div>
                    <div className="text-xs text-white/70 mb-1">Savings</div>
                    <div className="text-lg font-bold text-white">€180</div>
                  </div>
                </div>
              </div>
            </motion.div>

            {/* Progress to Next Tier */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.8, duration: 0.5 }}
              className="mb-6"
            >
              <div className="flex justify-between items-center mb-2">
                <span className="text-sm font-semibold text-gray-300">Progress to Gold</span>
                <span className="text-sm font-bold text-yellow-400">2,550 pts to go</span>
              </div>
              <div className="w-full h-3 bg-gray-800 rounded-full overflow-hidden">
                <motion.div
                  initial={{ width: 0 }}
                  animate={{ width: "49%" }}
                  transition={{ delay: 1.0, duration: 1.2, ease: "easeOut" }}
                  className="h-full bg-gradient-to-r from-yellow-400 to-yellow-500 rounded-full"
                />
              </div>
            </motion.div>

            {/* Earning Rules Section */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 1.2, duration: 0.5 }}
              className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-2xl p-4 mb-6"
            >
              <div className="text-sm font-bold text-white mb-3">💰 Earning Rules</div>
              
              {/* Base Rate */}
              <div className="flex items-center justify-between mb-3 pb-3 border-b border-white/10">
                <span className="text-xs text-gray-400">Base Rate</span>
                <div className="px-2.5 py-1 bg-gradient-to-r from-pink-500 to-fuchsia-500 rounded-full">
                  <span className="text-xs font-bold text-white">1 pt per €10</span>
                </div>
              </div>

              {/* Tier Multipliers */}
              <div className="mb-3">
                <div className="text-xs text-gray-400 mb-2">Tier Multipliers</div>
                <div className="grid grid-cols-3 gap-2">
                  <motion.div
                    initial={{ opacity: 0, scale: 0.8 }}
                    animate={{ opacity: 1, scale: 1 }}
                    transition={{ delay: 1.4, duration: 0.3 }}
                    className="bg-gradient-to-br from-orange-600/20 to-orange-700/20 border border-orange-600/30 rounded-xl p-2 text-center"
                  >
                    <div className="text-xs font-bold text-orange-300 mb-0.5">Bronze</div>
                    <div className="text-sm font-bold text-white">1×</div>
                  </motion.div>
                  <motion.div
                    initial={{ opacity: 0, scale: 0.8 }}
                    animate={{ opacity: 1, scale: 1 }}
                    transition={{ delay: 1.5, duration: 0.3 }}
                    className="bg-gradient-to-br from-gray-400/20 to-gray-500/20 border-2 border-gray-300 rounded-xl p-2 text-center"
                  >
                    <div className="text-xs font-bold text-gray-200 mb-0.5">Silver</div>
                    <div className="text-sm font-bold text-white">1.5×</div>
                  </motion.div>
                  <motion.div
                    initial={{ opacity: 0, scale: 0.8 }}
                    animate={{ opacity: 1, scale: 1 }}
                    transition={{ delay: 1.6, duration: 0.3 }}
                    className="bg-gradient-to-br from-yellow-500/20 to-yellow-600/20 border border-yellow-500/30 rounded-xl p-2 text-center"
                  >
                    <div className="text-xs font-bold text-yellow-300 mb-0.5">Gold</div>
                    <div className="text-sm font-bold text-white">2×</div>
                  </motion.div>
                </div>
              </div>

              {/* Goal Bonuses */}
              <div>
                <div className="text-xs text-gray-400 mb-2">Goal Bonuses</div>
                <div className="grid grid-cols-4 gap-1.5">
                  {[
                    { pct: '25%', pts: '+100' },
                    { pct: '50%', pts: '+250' },
                    { pct: '75%', pts: '+500' },
                    { pct: '100%', pts: '+1K' }
                  ].map((bonus, i) => (
                    <motion.div
                      key={i}
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: 1.7 + i * 0.05, duration: 0.3 }}
                      className="bg-gradient-to-br from-green-500/20 to-emerald-500/20 border border-green-500/30 rounded-lg p-1.5 text-center"
                    >
                      <div className="text-xs text-gray-300">{bonus.pct}</div>
                      <div className="text-xs font-bold text-green-400">{bonus.pts}</div>
                    </motion.div>
                  ))}
                </div>
              </div>
            </motion.div>

            {/* Top Benefits */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 2.0, duration: 0.5 }}
              className="mb-6"
            >
              <div className="text-sm font-bold text-white mb-3">🎁 Top Benefits</div>
              <div className="space-y-2">
                {/* Shopping */}
                <motion.div
                  whileHover={{ scale: 1.02 }}
                  className="flex items-center gap-3 bg-white/5 backdrop-blur-sm border border-white/10 rounded-xl p-3"
                >
                  <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-pink-500 to-rose-500 flex items-center justify-center text-xl">
                    🛍️
                  </div>
                  <div className="flex-1">
                    <div className="text-xs font-semibold text-white">Gift Cards</div>
                    <div className="text-xs text-gray-400">Top brands available</div>
                  </div>
                </motion.div>

                {/* Premium */}
                <motion.div
                  whileHover={{ scale: 1.02 }}
                  className="flex items-center gap-3 bg-white/5 backdrop-blur-sm border border-white/10 rounded-xl p-3"
                >
                  <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-purple-500 to-fuchsia-500 flex items-center justify-center text-xl">
                    💎
                  </div>
                  <div className="flex-1">
                    <div className="text-xs font-semibold text-white">Premium Features</div>
                    <div className="text-xs text-gray-400">Earn up to 1 pt per €1</div>
                  </div>
                </motion.div>
              </div>
            </motion.div>

            {/* Call to Action */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 2.4, duration: 0.5 }}
            >
              <motion.button
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                className="w-full bg-gradient-to-r from-pink-500 via-fuchsia-500 to-purple-600 text-white font-bold py-3 px-4 rounded-2xl shadow-lg"
              >
                View All Rewards →
              </motion.button>
            </motion.div>

            {/* Extra spacing for scroll animation */}
            <div className="h-32"></div>
          </motion.div>
        </div>
      </div>
    </div>
  )
}

export default PointsPhoneMockup
